/**
 * useAttribution — first-touch UTM + referrer capture with sessionStorage stickiness.
 *
 * Behavior:
 *  - On first mount, inspect window.location.search for utm_source, utm_campaign,
 *    utm_medium, utm_content, utm_term (case-insensitive on key lookup, stored
 *    lowercased).
 *  - Capture document.referrer (kept as-is).
 *  - If ANY UTM param is present on landing, persist the attribution object to
 *    sessionStorage under "zenme_attribution_v1".
 *  - If a prior attribution object already exists, DO NOT overwrite — first-touch
 *    wins so the original ad-click context survives cross-page navigation.
 *  - getAttribution() reads sessionStorage and returns the object (or defaults).
 */

const STORAGE_KEY = "zenme_attribution_v1";

const EMPTY_ATTRIBUTION = {
    utmSource: "",
    utmCampaign: "",
    utmMedium: "",
    utmContent: "",
    utmTerm: "",
    referrer: "",
    landingPath: "",
    landedAt: "",
};

const UTM_KEYS = [
    "utm_source",
    "utm_campaign",
    "utm_medium",
    "utm_content",
    "utm_term",
];

const readQueryParams = (search) => {
    if (typeof search !== "string" || !search) return {};
    try {
        const params = new URLSearchParams(search);
        const lookup = {};
        for (const [k, v] of params.entries()) {
            lookup[k.toLowerCase()] = v;
        }
        return lookup;
    } catch (e) {
        return {};
    }
};

export const captureAttribution = () => {
    if (typeof window === "undefined") return EMPTY_ATTRIBUTION;

    // First-touch wins — never overwrite an existing sticky attribution.
    try {
        const existing = window.sessionStorage.getItem(STORAGE_KEY);
        if (existing) {
            return JSON.parse(existing);
        }
    } catch (e) {
        // sessionStorage unavailable (private mode etc.) — fall through to fresh capture
    }

    const params = readQueryParams(window.location.search || "");
    const hasAnyUtm = UTM_KEYS.some((k) => Boolean(params[k]));

    const attribution = {
        utmSource: params.utm_source || "",
        utmCampaign: params.utm_campaign || "",
        utmMedium: params.utm_medium || "",
        utmContent: params.utm_content || "",
        utmTerm: params.utm_term || "",
        referrer: (typeof document !== "undefined" && document.referrer) || "",
        landingPath:
            (window.location.pathname || "") +
            (window.location.search || ""),
        landedAt: new Date().toISOString(),
    };

    // Only persist if there's something worth sticking (any UTM present).
    // Per spec: "if ANY UTM param is present on landing, persist".
    if (hasAnyUtm) {
        try {
            window.sessionStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(attribution),
            );
        } catch (e) {
            // best-effort only — silently skip if storage blocked
        }
    }

    return attribution;
};

export const getAttribution = () => {
    if (typeof window === "undefined") return { ...EMPTY_ATTRIBUTION };
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return { ...EMPTY_ATTRIBUTION, ...parsed };
        }
    } catch (e) {
        // ignore
    }
    return { ...EMPTY_ATTRIBUTION };
};

// React hook — runs captureAttribution() once on mount.
import { useEffect } from "react";

export const useAttribution = () => {
    useEffect(() => {
        captureAttribution();
    }, []);
};

export default useAttribution;
